import React from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  TextField,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Stack,
  Divider,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import _ from 'lodash';

// Utility functions
export const getMaterialTypes = (items) => {
  const materialTypes = [];
  items?.forEach((item) => {
    _.map(item?.order, (orderItem) => {
      materialTypes.push(orderItem?.material?.material_type?.name);
    });
  });
  return _.uniq(materialTypes);
};

export const getFilteredOrders = (items, materialFilter) => {
  if (materialFilter === 'all') return items;

  return items
    .map((item) => {
      const filteredOrderItems = item.order.filter(
        (orderItem) =>
          orderItem?.material?.material_type?.name === materialFilter,
      );

      return {
        ...item,
        order: filteredOrderItems,
      };
    })
    .filter((item) => item.order.length > 0); // เก็บเฉพาะ order ที่ยังมีสินค้าอยู่
};

export const separateByPlace = (orders) => {
  const oldWarehouse = [];
  const newWarehouse = [];
  const otherWarehouse = [];

  orders?.forEach((order) => {
    const oldItems = [];
    const newItems = [];
    const otherItems = [];

    order?.order?.forEach((item) => {
      if (item?.place === 'คลังรูปพรรณเก่า') {
        oldItems.push(item);
      } else if (item?.place === 'คลังรูปพรรณใหม่') {
        newItems.push(item);
      } else {
        otherItems.push(item);
      }
    });

    if (oldItems.length > 0) {
      oldWarehouse.push({ ...order, order: oldItems });
    }
    if (newItems.length > 0) {
      newWarehouse.push({ ...order, order: newItems });
    }
    if (otherItems.length > 0) {
      otherWarehouse.push({ ...order, order: otherItems });
    }
  });

  return { oldWarehouse, newWarehouse, otherWarehouse };
};

export const calculateTotals = (orders) => {
  const totalCost = _.sumBy(orders, (item) => _.sumBy(item?.order, 'cost'));
  const totalWeight = _.sumBy(orders, (item) => _.sumBy(item?.order, 'weight'));
  return { totalCost, totalWeight: totalWeight.toFixed(2) };
};

export const calculateNetTotals = (orders) => {
  const saleOrders =
    orders?.filter((order) => order?.gold_transaction_type === 'SALE') || [];
  const buyOrders =
    orders?.filter((order) => order?.gold_transaction_type === 'BUY') || [];

  const saleTotals = calculateTotals(saleOrders);
  const buyTotals = calculateTotals(buyOrders);

  return {
    totalCost: saleTotals.totalCost - buyTotals.totalCost,
    totalWeight: (
      parseFloat(saleTotals.totalWeight) - parseFloat(buyTotals.totalWeight)
    ).toFixed(2),
  };
};

// Filter Controls
export const FilterControls = ({
  materialTypes,
  currentFilter,
  setFilter,
  date,
  setDate,
}) => (
  <Card sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0' }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 3 }}
    >
      <Button
        variant="contained"
        startIcon={<ArrowBackIosIcon />}
        onClick={() =>
          setDate(dayjs(date).subtract(1, 'day').format('YYYY-MM-DD'))
        }
        sx={{ minWidth: 120 }}
      >
        ก่อนหน้า
      </Button>
      <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
        {dayjs(date).format('DD MMMM YYYY')}
      </Typography>
      <Button
        variant="contained"
        endIcon={<ArrowForwardIosIcon />}
        onClick={() => setDate(dayjs(date).add(1, 'day').format('YYYY-MM-DD'))}
        sx={{ minWidth: 120 }}
      >
        ถัดไป
      </Button>
    </Stack>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            renderInput={(props) => <TextField {...props} fullWidth />}
            label="เลือกวันที่"
            value={dayjs(date)}
            onChange={(newValue) => setDate(newValue.format('YYYY-MM-DD'))}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>ประเภทสินค้า</InputLabel>
          <Select
            value={currentFilter}
            label="ประเภทสินค้า"
            onChange={(e) => setFilter(e.target.value)}
          >
            <MenuItem value="all">ทั้งหมด</MenuItem>
            {materialTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  </Card>
);

export const WarehouseSummary = ({
  oldWarehouseOrders,
  newWarehouseOrders,
  otherWarehouseOrders,
  type,
  materialFilter,
}) => {
  const warehouses = [
    {
      name: 'คลังรูปพรรณเก่า',
      orders: oldWarehouseOrders,
      color: '#d32f2f',
    },
    {
      name: 'คลังรูปพรรณใหม่',
      orders: newWarehouseOrders,
      color: '#2e7d32',
    },
    {
      name: 'คลังอื่นๆ',
      orders: otherWarehouseOrders,
      color: '#1976d2',
    },
  ];

  const showWeight = materialFilter && materialFilter !== 'all';

  return (
    <Card
      sx={{
        p: 0,
        mb: 3,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Box
        sx={{
          p: 3,
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <AssessmentIcon
            sx={{
              color: '#1976d2',
              fontSize: 24,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
            }}
          >
            สรุปรายการตามคลัง
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {warehouses.map((warehouse, index) => {
            const totals =
              type === 'SUMMARY'
                ? calculateNetTotals(warehouse.orders)
                : calculateTotals(warehouse.orders);

            return (
              <Grid key={index} item xs={12} sm={6} md={4}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 3,
                    bgcolor: '#fafafa',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: warehouse.color,
                    }}
                  >
                    {warehouse.name}
                  </Typography>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        รายการ:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: warehouse.color,
                        }}
                      >
                        {warehouse.orders?.length || 0}
                      </Typography>
                    </Box>
                    {showWeight && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          น้ำหนัก:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: warehouse.color,
                          }}
                        >
                          {Number(totals.totalWeight).toLocaleString()} กรัม
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        มูลค่า:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: warehouse.color,
                        }}
                      >
                        {totals.totalCost.toLocaleString()} บาท
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Card>
  );
};

// Summary Card
export const SummaryCard = ({
  totals,
  type,
  count,
  orders,
  materialFilter,
}) => {
  const displayTotals =
    type === 'SUMMARY' || (type !== 'BUY' && type !== 'SALE')
      ? calculateNetTotals(orders)
      : totals;

  const showWeight = materialFilter !== 'all';

  const config = {
    BUY: {
      title: 'สรุปรายการซื้อ',
      color: '#d32f2f',
      icon: <TrendingDownIcon />,
    },
    SALE: {
      title: 'สรุปรายการขาย',
      color: '#2e7d32',
      icon: <TrendingUpIcon />,
    },
    SUMMARY: {
      title: 'สรุปรายการรวม',
      color: '#1976d2',
      icon: <AssessmentIcon />,
    },
  };

  const currentConfig = config[type] || config.SUMMARY;

  return (
    <Card sx={{ p: 0, mb: 3, border: '1px solid #e0e0e0' }}>
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          {React.cloneElement(currentConfig.icon, {
            sx: { color: currentConfig.color },
          })}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {currentConfig.title}ฟหกด
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={showWeight ? 4 : 6}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: '#fafafa',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                จำนวนรายการ
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: currentConfig.color }}
              >
                {count || 0}
              </Typography>
            </Box>
          </Grid>
          {showWeight && (
            <Grid item xs={4}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 2,
                  bgcolor: '#fafafa',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  น้ำหนักรวม
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: currentConfig.color }}
                >
                  {(displayTotals.totalWeight || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  กรัม
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={showWeight ? 4 : 6}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: '#fafafa',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                มูลค่ารวม
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: currentConfig.color }}
              >
                {(displayTotals.totalCost || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                บาท
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

// Order Table
export const OrderTable = ({
  orders,
  transactionType,
  onViewDetail,
  materialFilter,
}) => {
  const showWeight = materialFilter !== 'all';

  return (
    <Card sx={{ border: '1px solid #e0e0e0' }}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>ลำดับ</TableCell>
              {/* <TableCell sx={{ fontWeight: 600 }}>ประเภท</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>ลูกค้า</TableCell> */}
              <TableCell sx={{ fontWeight: 600 }}>รายการ</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>มูลค่า</TableCell>
              {showWeight && (
                <TableCell sx={{ fontWeight: 600 }}>จำนวน</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600 }}>วันที่</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>การดำเนินการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!_.isEmpty(orders) ? (
              orders.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Chip
                      label={row?.gold_transaction_type || 'ไม่ระบุ'}
                      color={
                        row?.gold_transaction_type === 'BUY'
                          ? 'error'
                          : 'success'
                      }
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {row?.customer?.name || 'ไม่ระบุ'}
                  </TableCell>
                  <TableCell>
                    {row?.order?.map((item, idx) => (
                      <Chip
                        key={idx}
                        label={
                          `${item?.material?.name} ${item?.material?.material_type?.name}` ||
                          'ไม่ระบุ'
                        }
                        variant="outlined"
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    {row?.order?.map((item, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                      >
                        {(item?.cost || 0).toLocaleString()} บาท
                      </Typography>
                    ))}
                  </TableCell>
                  {showWeight && (
                    <TableCell sx={{ fontWeight: 500 }}>
                      {_.sumBy(row?.order, 'weight').toLocaleString()} กรัม
                    </TableCell>
                  )}
                  <TableCell>{dayjs(row?.date).format('DD/MM/YYYY')}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DescriptionIcon />}
                      onClick={() => onViewDetail(row._id)}
                    >
                      ดูรายละเอียด
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={showWeight ? 8 : 7}
                  sx={{ textAlign: 'center', py: 6 }}
                >
                  <Typography variant="h6" color="text.secondary">
                    ไม่มีข้อมูลในวันที่เลือก
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export const SimpleOrderTable = ({
  orders,
  title,
  onViewDetail,
  showWeight = true,
  allData,
}) => {
  const totalWeight = _.sumBy(orders, (o) => _.sumBy(o.order, 'weight'));
  const totalCost = _.sumBy(orders, (o) => _.sumBy(o.order, 'cost'));
  const totalVat = _.sumBy(orders, (o) => _.sumBy(o.order, 'vat'));

  return (
    <Card
      sx={{
        mb: 3,
        border: '1px solid #e0e0e0',
        width: allData === false ? '100%' : '100%',
        maxWidth: allData === false ? '100%' : '1200px',
        mx: allData === false ? 0 : 'auto', // ทำให้รายการอยู่กลางแบบเต็ม
      }}
    >
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600 }}
          className={
            title === 'ขาย'
              ? 'text-green-800'
              : title === 'ซื้อ'
              ? 'text-red-700'
              : ''
          }
        >
          {title === 'ขาย'
            ? 'รายการขาย'
            : title === 'ซื้อ'
            ? 'รายการซื้อ'
            : title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {orders?.length || 0} รายการ
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600 }}>ลำดับ</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>สินค้า</TableCell>
              {showWeight && (
                <TableCell sx={{ fontWeight: 600 }}>น้ำหนัก</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600 }}>มูลค่า</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>ภาษี</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>การดำเนินการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!_.isEmpty(orders) ? (
              orders.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {row?.order?.map((item, idx) => (
                      <Typography key={idx} variant="body2">
                        {item?.material?.name}{' '}
                        {item?.material?.material_type?.name}
                      </Typography>
                    ))}
                  </TableCell>
                  {showWeight && (
                    <TableCell sx={{ fontWeight: 500 }}>
                      {_.sumBy(row?.order, 'weight').toLocaleString()} กรัม
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 500 }}>
                    {_.sumBy(row?.order, 'cost').toLocaleString()} บาท
                  </TableCell>
                  <TableCell>
                    {row?.order?.map((item, idx) => (
                      <Typography key={idx} variant="body2">
                        {item?.vat} บาท
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DescriptionIcon />}
                      onClick={() => onViewDetail && onViewDetail(row._id)}
                    >
                      ดู
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={showWeight ? 6 : 5}
                  sx={{ textAlign: 'center', py: 6 }}
                >
                  <Typography variant="h6" color="text.secondary">
                    ไม่มีข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* สรุปน้ำหนัก */}
            {orders?.length > 0 && showWeight && (
              <TableRow sx={{ bgcolor: '#e0e0e0' }}>
                <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
                  รวม
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {totalWeight.toLocaleString()} กรัม
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {totalCost.toLocaleString()} บาท
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {totalVat.toLocaleString()} บาท
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

// แยกตามคลังและ material type
export const WarehouseTables = ({
  oldWarehouseOrders,
  newWarehouseOrders,
  otherWarehouseOrders,
  onViewDetail,
  allData,
}) => {
  const groupByMaterialType = (orders) => {
    const flattened = _.flatten(
      orders?.map((o) => o.order?.map((i) => ({ ...o, order: [i] }))),
    );
    const sorted = _.sortBy(flattened, (o) => new Date(o.createdAt));

    const grouped = _.groupBy(
      sorted,
      (o) => o.order[0].material.material_type.name,
    );

    return Object.keys(grouped).map((type) => ({
      type,
      orders: grouped[type],
    }));
  };

  const allWarehouseGroups = [
    {
      name: 'คลังรูปพรรณเก่า',
      groups: groupByMaterialType(oldWarehouseOrders),
    },
    {
      name: 'คลังรูปพรรณใหม่',
      groups: groupByMaterialType(newWarehouseOrders),
    },
    { name: 'คลังอื่นๆ', groups: groupByMaterialType(otherWarehouseOrders) },
  ];

  return (
    <Grid container spacing={3}>
      {allWarehouseGroups.map((warehouse) => (
        <React.Fragment key={warehouse.name}>
          {/* หัวข้อ Warehouse */}
          {/* <Grid item xs={12}>
            <h2 className="text-xl font-bold mb-3">{warehouse.name}</h2>
          </Grid> */}

          {/* loop group.type */}
          {warehouse.groups.map((group) => {
            // แยกตามประเภทของ order
            const saleOrders = group.orders.filter(
              (o) => o.gold_transaction_type === 'SALE',
            );
            const buyOrders = group.orders.filter(
              (o) => o.gold_transaction_type === 'BUY',
            );

            return (
              <Grid item xs={12} key={`${warehouse.name} - ${group.type}`}>
                <div className="p-3 border rounded-lg bg-gray-50">
                  {/* หัวข้อ group.type */}
                  <h3 className="text-lg font-semibold mb-2">
                    {`${warehouse.name}-${group.type}`}
                  </h3>

                  <Grid container spacing={2}>
                    {/* === SALE === */}
                    {saleOrders.length > 0 && (
                      <Grid item xs={12} md={allData ? 6 : 12}>
                        <SimpleOrderTable
                          orders={saleOrders}
                          allData={allData}
                          title={`ขาย`}
                          onViewDetail={onViewDetail}
                        />
                      </Grid>
                    )}

                    {/* === BUY === */}
                    {buyOrders.length > 0 && (
                      <Grid item xs={12} md={allData ? 6 : 12}>
                        {/* {buyOrders.length > 0 && (
                          <h4 className="font-semibold mb-1 text-blue-700">
                            ซื้อ
                          </h4>
                        )} */}
                        <SimpleOrderTable
                          orders={buyOrders}
                          title={`ซื้อ`}
                          allData={allData}
                          onViewDetail={onViewDetail}
                        />
                      </Grid>
                    )}
                  </Grid>
                </div>
              </Grid>
            );
          })}
        </React.Fragment>
      ))}
    </Grid>
  );
};
